import { useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { articles, allArticlesList, type Article } from '@/mocks/articles';

const defaultArticle: Article = {
  id: 1,
  title: '英超新赛季前瞻：六大豪门实力分析与夺冠预测',
  category: '足球',
  categorySlug: 'football',
  date: '2026-08-05',
  excerpt: '2026-27赛季英超即将拉开帷幕，六大豪门经过夏季转会窗口的调整，实力格局是否发生改变？本文为您带来全面深入的分析。',
  image: 'https://readdy.ai/api/search-image?query=Premier%20League%20football%20stadium%20with%20players%20on%20pitch%2C%20dramatic%20lighting%2C%20professional%20sports%20photography&width=1200&height=600&seq=art-def-01&orientation=landscape',
  author: '体育观察员',
  views: 12800,
  tags: ['英超', '足球', '赛季前瞻'],
  content: '<p>文章内容加载中...</p>',
  relatedArticles: [],
};

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' as const });
  }, [id]);

  const article = useMemo(() => (id ? articles[Number(id)] : undefined) || defaultArticle, [id]);

  const relatedArticles = useMemo(() => {
    return article.relatedArticles
      .map((rid) => allArticlesList.find((a) => a.id === rid))
      .filter(Boolean) as typeof allArticlesList;
  }, [article.relatedArticles]);

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    image: article.image,
    author: {
      '@type': 'Person',
      name: article.author,
    },
    datePublished: article.date,
    publisher: {
      '@type': 'Organization',
      name: 'bb体育',
      url: 'https://www.bbsports.com',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://www.bbsports.com/article/${article.id}`,
    },
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://www.bbsports.com' },
      { '@type': 'ListItem', position: 2, name: article.category, item: `https://www.bbsports.com/category/${article.categorySlug}` },
      { '@type': 'ListItem', position: 3, name: article.title, item: `https://www.bbsports.com/article/${article.id}` },
    ],
  };

  return (
    <>
      <SEOHead
        title={`${article.title} - bb体育`}
        description={article.excerpt}
        keywords={`bb体育,${article.category},${article.tags.join(',')},体育资讯,赛事分析`}
        canonicalUrl={`https://www.bbsports.com/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        schema={{ ...articleSchema, ...breadcrumbSchema }}
      />

      <Navbar />

      <main className="pt-20 md:pt-24">
        <div className="container-custom">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: article.category, path: `/category/${article.categorySlug}` },
              { label: article.title },
            ]}
          />
        </div>

        <article className="pb-16 md:pb-20">
          <div className="container-custom max-w-4xl">
            <div className="mb-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="px-3 py-1 rounded-full bg-primary-100 text-primary-600 text-xs font-medium whitespace-nowrap">
                  {article.category}
                </span>
                <span className="text-sm text-foreground-400 whitespace-nowrap">{article.date}</span>
                <span className="text-sm text-foreground-400 flex items-center gap-1">
                  <i className="ri-eye-line"></i> {article.views.toLocaleString()}
                </span>
              </div>

              <h1 className="font-heading text-2xl md:text-4xl lg:text-5xl font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex items-center gap-3 py-4 border-y border-background-200/70">
                <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <i className="ri-user-line text-primary-600"></i>
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground-800">{article.author}</div>
                  <div className="text-xs text-foreground-400">bb体育特约作者</div>
                </div>
              </div>
            </div>

            <div className="mb-10 rounded-xl overflow-hidden">
              <img
                src={article.image}
                alt={article.title}
                className="w-full h-auto object-cover"
                title={article.title}
              />
            </div>

            <div
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:text-sm md:prose-p:text-base
                prose-strong:text-foreground-900
                prose-blockquote:border-l-primary-500 prose-blockquote:bg-background-100 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:text-foreground-600
                prose-li:text-foreground-700
              "
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1.5 rounded-md bg-background-100 text-foreground-600 text-xs hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    # {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="container-custom max-w-4xl">
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 md:mb-8">
                相关推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {relatedArticles.map((item) => (
                  <Link
                    key={item.id}
                    to={`/article/${item.id}`}
                    className="group bg-background-50 rounded-xl border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="h-36 overflow-hidden">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        title={item.title}
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-xs font-medium text-primary-600 bg-primary-100 px-2 py-0.5 rounded-full whitespace-nowrap">
                          {item.category}
                        </span>
                        <span className="text-xs text-foreground-400 whitespace-nowrap">{item.date}</span>
                      </div>
                      <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-600 transition-colors duration-200">
                        {item.title}
                      </h3>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}